import { type Conversation, type ConversationUpdateReason, type Paginator, Client as TwilioChatClient } from '@twilio/conversations';

import {
  type CallbackTeardown,
  type ChatCreatedCallback,
  type ChatRemovedCallback,
  type ChatUpdatedCallback,
  type ConnectionStateChangedCallback,
  type InitFailedCallback,
} from './chat-client-listeners';
import { Chat } from './chat';
import { type ChatMeta } from './chat-meta';

/**
 * Chat client.
 *
 * Actually, it's just a wrapper around the `@twilio/conversations` client, and we can use any other chat library.
 * We prefer not to expose the underlying client directly to the rest of the app
 * to avoid layers leaking and tight coupling with the library.
 */
export class ChatClient {
  private readonly client: TwilioChatClient;

  public constructor(accessToken: string) {
    // We can specify `logLevel` according to our needs or environment.
    this.client = new TwilioChatClient(accessToken, { logLevel: 'warn' });
  }

  /** Get all chats related to the current user. */
  public async getChats(): Promise<Chat[]> {
    const paginator = await this.client.getSubscribedConversations();
    const conversations = await this.getAllConversationsFromPaginator(paginator);

    const chats = await Promise.all(conversations.map(conversation => this.fromConversation(conversation)));

    const sorted = chats.toSorted(Chat.compare);
    return sorted;
  }

  /**
   * Get chat by ID.
   * @param chatId Chat ID.
   */
  public async getChatById(chatId: ChatMeta['id']): Promise<Chat | null> {
    const chats = await this.getChats();
    return chats.find(chat => chat.id === chatId) ?? null;
  }

  /**
   * Update token for chat's client.
   * @param newToken New chat token.
   */
  public async updateToken(newToken: string): Promise<void> {
    await this.client.updateToken(newToken);
  }

  /** Shutdown chat's client. */
  public async shutdown(): Promise<void> {
    this.client.removeAllListeners();
    await this.client.shutdown();
  }

  /**
   * Register callback for `initFailed` event.
   * @param callback 'initFailed' event callback.
   */
  public onInitFailed(callback: InitFailedCallback): CallbackTeardown {
    this.client.on('initFailed', callback);
    return () => this.client.removeListener('initFailed', callback);
  }

  /**
   * Register callback for `initialized` event.
   * @param callback 'initialized' event callback.
   */
  public onInitialized(callback: () => void): CallbackTeardown {
    this.client.on('initialized', callback);
    return () => this.client.removeListener('initialized', callback);
  }

  /**
   * Register callback for `tokenExpired` event.
   * @param callback 'tokenExpired' event callback.
   */
  public onTokenExpired(callback: () => void): CallbackTeardown {
    this.client.on('tokenExpired', callback);
    return () => this.client.removeListener('tokenExpired', callback);
  }

  /**
   * Register callback for `tokenAboutToExpire` event.
   * @param callback 'tokenAboutToExpire' event callback.
   */
  public onTokenAboutToExpire(callback: () => void): CallbackTeardown {
    this.client.on('tokenAboutToExpire', callback);
    return () => this.client.removeListener('tokenAboutToExpire', callback);
  }

  /**
   * Register callback for `connectionStateChanged` event.
   * @param callback 'connectionStateChanged' event callback.
   */
  public onConnectionStateChanged(callback: ConnectionStateChangedCallback): CallbackTeardown {
    this.client.onWithReplay('connectionStateChanged', callback);
    return () => this.client.removeListener('connectionStateChanged', callback);
  }

  /**
   * Register callback for `conversationUpdated` event.
   * @param callback 'conversationUpdated' event callback.
   */
  public onChatUpdated(callback: ChatUpdatedCallback): CallbackTeardown {
    const innerCallback = async ({
      conversation,
      updateReasons,
    }: Readonly<{
      conversation: Conversation;
      updateReasons: ConversationUpdateReason[];
    }>): Promise<void> => {
      const chat = await this.fromConversation(conversation);
      callback({ chat, chatUpdateReasons: updateReasons });
    };

    this.client.addListener('conversationUpdated', innerCallback);

    return () => {
      this.client.removeListener('conversationUpdated', innerCallback);
    };
  }

  /**
   * Register callback for `conversationAdded` event.
   * @param callback 'conversationAdded' event callback.
   */
  public onChatCreated(callback: ChatCreatedCallback): CallbackTeardown {
    const innerCallback = async (conversation: Conversation): Promise<void> => {
      const chat = await this.fromConversation(conversation);
      callback(chat);
    };

    this.client.addListener('conversationAdded', innerCallback);

    return () => {
      this.client.removeListener('conversationAdded', innerCallback);
    };
  }

  /**
   * Register callback for `conversationRemoved` event.
   * @param callback 'conversationRemoved' event callback.
   */
  public onChatRemoved(callback: ChatRemovedCallback): CallbackTeardown {
    const innerCallback = async (conversation: Conversation): Promise<void> => {
      const chat = await this.fromConversation(conversation);
      callback(chat);
    };

    this.client.addListener('conversationRemoved', innerCallback);

    return () => {
      this.client.removeListener('conversationRemoved', innerCallback);
    };
  }

  private async getAllConversationsFromPaginator(paginator: Paginator<Conversation>): Promise<Conversation[]> {
    let conversations: Conversation[] = paginator.items;
    let currentPage = paginator;

    while (paginator.hasNextPage) {
      // eslint-disable-next-line no-await-in-loop
      currentPage = await currentPage.nextPage();
      conversations = conversations.concat(currentPage.items);
    }

    return conversations;
  }

  private fromConversation(conversation: Conversation): Promise<Chat> {
    return Chat.fromTwilioConversation(conversation, this.client);
  }
}
